import { cn, getAdminUrl, getProjectId } from "@/lib/utils.ts";
import { EntLinkifiedText } from "./EntLinkifiedText.tsx";
import type { AiDebugScope, AiDebugScopeAction, GetMessageResp } from "@/types/messages.ts";
import { autoMessageTypes } from "@/types/messages.ts";
import { useDebugContext } from "@/contexts/DebugContext.tsx";
import { Bug, Check, ChevronDown, Clipboard, ExternalLink, Trash, X } from "lucide-react";
import hljs from "highlight.js";
import "highlight.js/styles/github-dark-dimmed.css";
import { useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useGateValue } from "@statsig/react-bindings";
import { useCurrentUserContext } from "../../hooks/useUser.tsx";
import { Markdown } from "../markdown/Markdown.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible.tsx";

const traceViewerUrl = (traceId: string) => {
  const projectId = getProjectId();
  return import.meta.env.DEV
    ? `http://localhost:16686/trace/${traceId}`
    : `https://console.cloud.google.com/traces/explorer;traceId=${traceId}?project=${projectId}`;
};

const HighlightedCode = ({ html }: { html: string }) => (
  // biome-ignore lint/security/noDangerouslySetInnerHtml: highlight.js output from our own debug data
  <code dangerouslySetInnerHTML={{ __html: html }} />
);

const CodeBlock = ({ code, language }: { code: string; language: string }) => {
  const highlightedHtml = useMemo(() => {
    const lang = language.toLowerCase();
    if (hljs.getLanguage(lang)) {
      return hljs.highlight(code, { language: lang }).value;
    }
    return hljs.highlightAuto(code).value;
  }, [code, language]);

  return (
    <div className="rounded bg-zinc-900 text-zinc-100 text-xs font-mono overflow-x-auto">
      <div className="px-3 py-1 text-2xs text-zinc-500 font-sans font-medium border-b border-zinc-700/50">
        {language}
      </div>
      <pre className="px-3 py-2 whitespace-pre-wrap hljs">
        <HighlightedCode html={highlightedHtml} />
      </pre>
    </div>
  );
};

const CodeTag = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="text-xs bg-muted px-2 py-1 rounded-md inline-block hover:bg-muted/50 transition-colors">
      {children}
    </div>
  );
};

const StatusBadge = ({ status }: { status: NonNullable<AiDebugScope["status"]>["status"] }) => {
  const colors: Record<NonNullable<AiDebugScope["status"]>["status"], string> = {
    success: "bg-green-500 text-white",
    partial_success: "bg-yellow-500 text-white",
    failure: "bg-red-500 text-white",
  };

  return (
    <span className={cn("text-2xs px-2 py-0.5 rounded-full font-medium", colors[status])}>
      {status}
    </span>
  );
};

/**
 * New recursive rendering component for AiDebugScope
 * Handles the hierarchical structure with visual nesting
 */
const DebugScopeView = ({ scope, depth }: { scope: AiDebugScope; depth: number }) => {
  // special case - the root scope tends to be empty, aside from child scopes. If so,
  // we want to avoid a layer of nesting and just render a flat list of its child scopes.
  // however, if it does have other data, we should just render it normally.
  const scopeKeys = Object.keys(scope);
  const hasOnlyLabelAndScopes =
    scopeKeys.length <= 3 &&
    scopeKeys.every((key) => key === "label" || key === "scopes" || key === "isRoot");
  const flattenSelf = depth === 0 && scope.isRoot && hasOnlyLabelAndScopes;
  if (flattenSelf && scope.scopes) {
    return (
      <>
        {scope.scopes.map((childScope, index) => (
          <div key={index}>
            <DebugScopeView scope={childScope} depth={0} />
          </div>
        ))}
      </>
    );
  }

  return (
    <Collapsible defaultOpen={depth <= 1}>
      <div className="space-y-2">
        {/* Main scope info with collapsible trigger */}
        <CollapsibleTrigger className="flex items-center w-full text-left CollapsibleTrigger gap-0.5">
          <div className="font-medium text-xs text-blue-600">Scope: {scope.label}</div>
          <ChevronDown className="h-4 w-4 shrink-0 text-blue-600 transition-transform duration-200 CollapsibleTriggerIcon" />
          {scope.status && <StatusBadge status={scope.status.status} />}
        </CollapsibleTrigger>

        <CollapsibleContent>
          {scope.body && (
            <div className="text-sm text-muted-foreground">
              <EntLinkifiedText text={scope.body} />
            </div>
          )}

          {/* Status info if present */}
          {scope.status && (
            <>
              <div className="text-sm">
                <span className="font-medium">Request:</span>{" "}
                <EntLinkifiedText text={scope.status.request} />
              </div>
              <div className="text-sm">
                <span className="font-medium">Status:</span>{" "}
                <EntLinkifiedText text={scope.status.statusMessage} />
              </div>
            </>
          )}

          {/* Forthic stack if present */}
          {scope.forthicStack && (
            <div className="space-y-2">
              <span className="font-medium">Stack:</span> {scope.forthicStack.description}
              <Collapsible>
                <CollapsibleTrigger className="flex items-center CollapsibleTrigger text-sm">
                  <span className="font-medium text-gray-500 text-xs">
                    <EntLinkifiedText text={scope.forthicStack.urn} />
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CodeBlock
                    code={JSON.stringify(scope.forthicStack.data, null, 2)}
                    language="JSON"
                  />
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Actions list with visual nesting */}
          {scope.actions && scope.actions.length > 0 && (
            <div className="space-y-3 mt-3">
              {scope.actions.map((action, index) => (
                <div key={index} className="border-l-2 border-emerald-500 pl-4">
                  <DebugScopeActionView action={action} />
                </div>
              ))}
            </div>
          )}

          {/* Nested scopes with recursive rendering */}
          {scope.scopes && scope.scopes.length > 0 && (
            <div className="space-y-3 mt-3">
              {scope.scopes.map((childScope, index) => (
                <div key={index} className={cn("border-l-2 pl-4 border-blue-400")}>
                  <DebugScopeView scope={childScope} depth={depth + 1} />
                </div>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

/**
 * Build a SERP console URL from a search action's description and accountId.
 */
function buildSerpUrl(description: string, accountId: number): string {
  const query = description.replace(/^\[.*?\]\s*/, "");
  const params = new URLSearchParams();
  params.set("q", query);
  params.set("userId", String(accountId));
  return `${getAdminUrl()}/admin/serp?${params.toString()}`;
}

/**
 * Component for rendering individual debug actions within a scope
 */
const DebugScopeActionView = ({ action }: { action: AiDebugScopeAction }) => {
  const { accountId: debugAccountId } = useDebugContext();
  const { userContext } = useCurrentUserContext();
  const accountId = debugAccountId ?? userContext?.user.id;
  const serpUrl =
    action.label === "search" && action.description && accountId != null
      ? buildSerpUrl(action.description, accountId)
      : null;

  return (
    <div className="text-sm space-y-1">
      <div className="font-medium text-xs text-emerald-700 flex items-center gap-2">
        <span>Action: {action.label}</span>
        {action.durationMs !== undefined && (
          <span className="text-zinc-500 font-normal">
            {action.durationMs >= 1000
              ? `${(action.durationMs / 1000).toFixed(1)}s`
              : `${action.durationMs}ms`}
          </span>
        )}
      </div>
      {action.description && (
        <div className="text-muted-foreground">
          <EntLinkifiedText text={action.description} />
        </div>
      )}
      {serpUrl && (
        <a
          href={serpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          SERP Console
          <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {action.longContent && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center CollapsibleTrigger text-xs font-medium">
            <span className="font-medium">Content</span>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="text-muted-foreground mt-1 whitespace-pre-wrap">
              <EntLinkifiedText text={action.longContent} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {action.json && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center CollapsibleTrigger text-xs font-medium">
            <span className="font-medium">Json</span>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CodeBlock code={JSON.stringify(action.json, null, 2)} language="JSON" />
          </CollapsibleContent>
        </Collapsible>
      )}

      {action.ai?.thought && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center CollapsibleTrigger text-xs font-medium">
            <span className="font-medium">AI Thought</span>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="italic text-muted-foreground mt-1">
              <Markdown>{action.ai.thought}</Markdown>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {action.ai?.examples && action.ai.examples.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center CollapsibleTrigger text-xs font-medium">
            <span className="font-medium">Examples ({action.ai.examples.length})</span>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 CollapsibleTriggerIcon" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-1 space-y-2">
              {action.ai.examples.map((example, idx) => (
                <CodeBlock code={JSON.stringify(example, null, 2)} language="JSON" key={idx} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {action.code && <CodeBlock code={action.code.code} language={action.code.language} />}

      {action.forthic && (
        <div className="space-y-2">
          <CodeBlock code={action.forthic.code} language="Forthic" />

          {action.forthic.modules && action.forthic.modules.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-1">
              <span className="font-medium">Forthic Modules:</span>
              {action.forthic.modules.toSorted().map((item, idx) => (
                <CodeTag key={idx}>{item}</CodeTag>
              ))}
            </div>
          )}
          {action.forthic.words && action.forthic.words.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-1">
              <span className="font-medium">Forthic Words:</span>
              {action.forthic.words.toSorted().map((item, idx) => (
                <CodeTag key={idx}>{item}</CodeTag>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CopyDebugButton = ({ debugData }: { debugData: NonNullable<GetMessageResp["debug"]> }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (e) {
          console.error("Failed to copy debug data:", e);
        }
      }}
      className="p-1 rounded-full hover:bg-muted transition-colors"
      aria-label="Copy debug data as JSON"
    >
      {copied ? <Check className="size-4 text-green-600" /> : <Clipboard className="size-4" />}
    </button>
  );
};

/**
 * Content component for the debug panel that contains all the debug information.
 * Extracted into a separate component to help with error boundary behavior.
 */
const AdminDebugPanelContent = ({
  debugData,
  message,
}: {
  debugData: NonNullable<GetMessageResp["debug"]>;
  message: GetMessageResp;
}) => {
  const isAutoMessage = autoMessageTypes.safeParse(message.type).success;

  return (
    <>
      {isAutoMessage && message.content && (
        <div className="flex flex-col gap-4">
          <p className="font-medium text-xs text-blue-600">Content</p>
          <div className="text-sm prose -my-4">
            <Markdown>{message.content}</Markdown>
          </div>
        </div>
      )}

      {!!debugData.plan && (
        <div className="flex flex-col gap-4">
          <p className="font-medium text-xs text-blue-600">Plan</p>
          <div className="text-sm prose -my-4">
            <Markdown>{debugData.plan}</Markdown>
          </div>
        </div>
      )}

      {/* AI Scopes section - hierarchical debug structure */}
      {/* Backwards compat: use scopes if available, otherwise fallback to [aiScope] */}
      {(() => {
        const scopes =
          debugData.scopes && debugData.scopes.length > 0
            ? debugData.scopes
            : debugData.aiScope
              ? [debugData.aiScope]
              : [];

        return (
          scopes.length > 0 && (
            <div className="space-y-4 text-sm">
              {scopes.map((scope, index) => (
                <DebugScopeView key={index} scope={scope} depth={0} />
              ))}
            </div>
          )
        );
      })()}

      {debugData.traceIds && debugData.traceIds.length > 0 && (
        <div>
          <p className="font-medium text-xs pb-2 text-blue-600">Trace IDs</p>
          <ul className="space-y-1">
            {[...new Set(debugData.traceIds)].map((traceId) => (
              <li key={traceId} className="text-xs">
                <CodeTag>
                  <a href={traceViewerUrl(traceId)} target="_blank" rel="noopener noreferrer">
                    {traceId}
                  </a>
                </CodeTag>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

/**
 * Displays a collapsible list of debug information including AI/Ax calls and plans.
 * Only visible to admin users and only shows when there is debug data present.
 *
 * Kept around for pre-agent-v4 threads. Newer threads disable this panel
 * by omission: the server skips hydrating `message.debug` for them (see
 * `getThreadDebugData` in `api/src/context/threads.ts`), so `debugData` is
 * undefined here and the component returns null. Tracked for full removal in
 * BIG-3887: https://linear.app/big-basin-labs/issue/BIG-3887/kill-read-paths-for-legacy-debug-panel
 */
export const AdminDebugPanel = ({
  message,
  isDebugOpen = false,
  onToggleDebug,
  onDelete,
  isUserMessage = false,
}: {
  message: GetMessageResp;
  isDebugOpen?: boolean;
  onToggleDebug: (isOpen: boolean | ((prev: boolean) => boolean)) => void;
  onDelete: (messageId: number) => void;
  isUserMessage?: boolean;
}) => {
  const isAdmin = useGateValue("internal-features");
  const debugData = isAdmin ? message.debug : null;

  // admins should always have debug data, non admins should never have it (filtered on the api)
  if (!isAdmin || !debugData) {
    return null;
  }

  return (
    <Collapsible open={isDebugOpen} onOpenChange={onToggleDebug}>
      {/* absolutely positioned container, positioned on left for agent messages or right for user messages */}
      <div
        className={cn(
          "absolute top-0.5 bottom-0 none md:flex flex-col justify-start z-10",
          isUserMessage
            ? "-right-7 pl-2" // right side for user messages, padding on left
            : "-left-7 pr-2", // left side for agent messages, padding on right
        )}
      >
        <CollapsibleTrigger
          className={cn(
            "bg-blue-500 text-white rounded-full size-5 flex justify-center items-center hover:bg-blue-400 cursor-pointer",
            // translate positioning here is purely for the animation, the actual position offset is from the parent
            "opacity-0 group-hover/message:opacity-100 invisible group-hover/message:visible transition-all duration-200 ease-out",
            // animation positioning based on message type
            isUserMessage
              ? "-translate-x-2 group-hover/message:translate-x-0" // slide from left to center for user messages
              : "translate-x-2 group-hover/message:-translate-x-0", // slide from right to center for agent messages
          )}
        >
          <Bug className="size-4" />
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="border rounded-2xl bg-popover text-popover-foreground border-blue-400">
          <div className="p-4 flex flex-col gap-6 relative">
            <div className="absolute top-2 right-2 flex items-center gap-2">
              <CopyDebugButton debugData={debugData} />
              <button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this message?")) {
                    onDelete(message.id);
                  }
                }}
                className="p-1 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                aria-label="Delete message"
              >
                <Trash className="size-4" />
              </button>
              <button
                onClick={() => onToggleDebug(false)}
                className="p-1 rounded-full hover:bg-muted transition-colors"
                aria-label="Close debug panel"
              >
                <X className="size-4" />
              </button>
            </div>
            <ErrorBoundary
              onError={(error) => {
                console.error("Debug panel crashed:", error);
              }}
              fallbackRender={({ error }) => (
                <div className="p-4 border rounded-2xl border-destructive">
                  <p className="text-destructive text-sm font-medium">
                    Debug panel crashed. Unfortunately there is no debug debug panel.
                  </p>
                  <p className="text-destructive text-xs mt-2 font-mono">
                    {error instanceof Error ? error.message : String(error)}
                  </p>
                </div>
              )}
            >
              <AdminDebugPanelContent debugData={debugData} message={message} />
            </ErrorBoundary>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
